import { ObjectUrlService } from './../../services/object-url/object-url.service';
import { FileExtension } from './../../models/file-type';
import { AlertService } from './../../services/alert/alert.service';
import { TranslateService } from '@ngx-translate/core';
import { ModalController, Platform } from '@ionic/angular';
import { Component, Input, ViewChild, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { NgxDropzoneComponent } from 'ngx-dropzone';
import { File as WGFile } from 'src/app/services/questionnaire/questionnaire.service';
import { RejectedFile } from 'ngx-dropzone/lib/ngx-dropzone.service';
import { PinCodeService } from 'src/app/services/pin-code/pin-code.service';
import { filter } from 'rxjs';

const SUPPORTED_IMAGE_FORMATS = ['JPG', 'PNG', 'GIF'];
const SUPPORTED_VIDEO_FORMATS = ['MP4', 'MOV', 'WEBM', 'MPEG', 'FLV', 'WMV', 'AVI'];
export const DEFAULT_VIDEO_THUMBNAIL = '/assets/images/default-video-thumbnail.svg';
@Component({
  selector: 'app-file-upload',
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.scss'],
})
export class FileUploadComponent implements OnDestroy, OnInit {
  @Input() title = '';
  @Input() subtitle = '';
  @Input() tapToUploadText = '';
  @Input() maxFileSizeMb: number;
  @Input() fileType: SupportedFileUploadType = SupportedFileUploadType.image;
  @ViewChild('inputFile') inputFile: ElementRef;
  @ViewChild('filePreview') filePreview: ElementRef;
  @ViewChild('dropzone') dropzone: NgxDropzoneComponent;
  accept: string;
  maxFileSizeBytes: string;
  file: WGFile;
  maxFilenameLength = 20;
  intervalFilePreview;
  intervalInputFile;
  errors: string[] = [];
  processingFile = false;
  isNativeFilePickerOpening = false;
  private DEFAULT_MAX_FILE_SIZE_PHOTO = 15;
  private DEFAULT_MAX_FILE_SIZE_VIDEO = 100;

  constructor(
    private modalCtrl: ModalController,
    private translateSvc: TranslateService,
    public alertSvc: AlertService,
    private objectURLsvc: ObjectUrlService,
    private platform: Platform,
    private pincodeSvc: PinCodeService
  ) {}

  ngOnInit() {
    if (this.fileType === SupportedFileUploadType.image) {
      this.accept = [FileExtension.jpeg, FileExtension.jpg, FileExtension.png, FileExtension.gif].join(',');
      if (!this.maxFileSizeMb) {
        this.maxFileSizeMb = this.DEFAULT_MAX_FILE_SIZE_PHOTO;
      }
    } else if (this.fileType === SupportedFileUploadType.video) {
      this.accept = [
        FileExtension.mov,
        FileExtension.flv,
        FileExtension.webm,
        FileExtension.wmv,
        FileExtension.mp4,
        FileExtension.mpeg,
        FileExtension.avi,
        FileExtension.avi2,
      ].join(',');
      if (!this.maxFileSizeMb) {
        this.maxFileSizeMb = this.DEFAULT_MAX_FILE_SIZE_VIDEO;
      }
    }
    // convert MB to bytes
    this.maxFileSizeBytes = String(this.maxFileSizeMb * 1048576);
    this.removeVideoFileTypeRestriction();

    this.platform.resume.pipe(filter(() => this.isNativeFilePickerOpening)).subscribe(async () => {
      // give the timeout because the other process must be finished first
      setTimeout(() => {
        this.isNativeFilePickerOpening = false;
        this.pincodeSvc.setAutoLock(true);
      }, 500);
    });
  }

  removeVideoFileTypeRestriction() {
    // on android, we couldnt record a new video if input accept=video
    // so we need to remove that accept attr
    if (this.platform.is('android') && this.fileType === SupportedFileUploadType.video) {
      this.intervalInputFile = setInterval(() => {
        const input = document.querySelector('ngx-dropzone input[type=file]');
        if (input) {
          input.removeAttribute('accept');
          this.intervalInputFile = clearInterval(this.intervalInputFile);
        }
      }, 100);
    }
  }

  async dismissModal() {
    if (this.file) {
      const option = await this.alertSvc.presentCustomConfirmation(
        this.translateSvc.instant('FileUpload.leaveConfirmation'),
        '',
        this.translateSvc.instant('FileUpload.yourUploadWillBeRemovedIfModalClosed'),
        this.translateSvc.instant('FileUpload.leave'),
        this.translateSvc.instant('FileUpload.stay'),
        ''
      );

      if (option.option) {
        return;
      }
    } else {
      await this.alertSvc.presentAlertConfirmation('', '', this.translateSvc.instant('FileUpload.pleaseUploadFile'));
    }
    this.modalCtrl.dismiss();
  }

  onResize() {
    this.setMaxFilenameLength();
  }

  ngOnDestroy() {
    this.intervalFilePreview = clearInterval(this.intervalFilePreview);
    this.intervalInputFile = clearInterval(this.intervalInputFile);
  }

  waitAndSetMaxFilenameLength(): void {
    // wait until file-preview is rendered so we can set maxFilenameLength based on client width
    this.intervalFilePreview = setInterval(() => {
      /* istanbul ignore else. Reason: hard to cover it */
      if (this.filePreview) {
        this.intervalFilePreview = clearInterval(this.intervalFilePreview);
        this.setMaxFilenameLength();
      }
    }, 100);
  }

  setMaxFilenameLength(): void {
    const width = this.getFilePreviewClientWidth();
    if (width) {
      let factor = 15;
      if (width >= 510) {
        factor = 11;
      } else if (width >= 375) {
        factor = 12;
      } else if (width >= 300) {
        factor = 13;
      } else if (width >= 265) {
        factor = 14;
      }
      this.maxFilenameLength = Math.floor(width / factor);
    }
  }

  /* istanbul ignore next. Reason not needed */
  getFilePreviewClientWidth(): number {
    return this.filePreview.nativeElement.clientWidth;
  }

  chooseFile() {
    if (this.platform.is('cordova')) {
      this.pincodeSvc.setAutoLock(false);
      this.isNativeFilePickerOpening = true;
    }
  }

  async onSelect(event: EventSelectedFile) {
    if (!this.validateSelectedFile(event)) {
      return;
    }
    this.processingFile = true;
    this.deleteFile(true);
    const f = event.addedFiles.pop();
    this.file = {
      name: f.name,
      type: f.type,
      size: f.size,
      fullPath: this.objectURLsvc.createObjectURL(f),
      logo: DEFAULT_VIDEO_THUMBNAIL,
    };
    this.processingFile = false;
    this.setVideoThumbnail(this.file);
    this.waitAndSetMaxFilenameLength();
  }

  validateSelectedFile(event: EventSelectedFile): boolean {
    this.errors = [];
    if (event.rejectedFiles.length + event.addedFiles.length > 1) {
      this.errors.push(this.translateSvc.instant('FileUpload.only1FileAccepted'));
      return false;
    }
    if (event.rejectedFiles.length) {
      event.rejectedFiles.forEach(rf => {
        if (rf.reason === 'type') {
          const supportedFormats = this.fileType === SupportedFileUploadType.image ? SUPPORTED_IMAGE_FORMATS : SUPPORTED_VIDEO_FORMATS;
          this.errors.push(this.translateSvc.instant('FileUpload.fileNotSupported', { formats: supportedFormats.join(', ') }));
        } else if (rf.reason === 'size') {
          const tooLargeMsgKey = this.fileType === SupportedFileUploadType.image ? 'photoTooLarge' : 'videoTooLarge';
          this.errors.push(this.translateSvc.instant('FileUpload.' + tooLargeMsgKey, { size: this.maxFileSizeMb + ' MB' }));
        }
      });
      return false;
    }
    return true;
  }

  submit(): void {
    this.modalCtrl.dismiss({ file: this.file });
  }

  async deleteFile(force: boolean = false) {
    if (!this.file) {
      return;
    }

    if (!force) {
      const option = await this.alertSvc.presentCustomConfirmation(
        '',
        '',
        this.translateSvc.instant('FileUpload.deleteConfirmation'),
        this.translateSvc.instant('FileUpload.cancel'),
        this.translateSvc.instant('FileUpload.yesDelete'),
        ''
      );

      if (!option.option) {
        return;
      }
    }

    this.errors = [];
    this.objectURLsvc.revokeObjectURL(this.file.fullPath);
    this.file = null;
  }

  replaceFile(): void {
    this.dropzone._fileInput.nativeElement.click();
  }

  setVideoThumbnail(file: WGFile) {
    // TODO: find a way to generate thumbnail from video file in ios & android
    // because this method is not working on cordova
    if (this.platform.is('cordova')) {
      return;
    }

    const videoElem = document.getElementById('video-element') as HTMLVideoElement;
    if (this.fileType === 'video' && videoElem.canPlayType(file.type)) {
      const canvasElem = this.prepareCanvasForThumbnailCapture(videoElem, file);
      const canvasCtx = canvasElem.getContext('2d');
      let frameCaptured = 0;

      /* istanbul ignore next. Readon: hard to trigger event */
      const captureThumb = () => {
        frameCaptured++;
        // first 3 captured frame by Chrome is always blank
        if (frameCaptured <= 3) {
          return;
        }
        canvasCtx.drawImage(videoElem, 0, 0, videoElem.videoWidth, videoElem.videoHeight);
        canvasElem.toBlob(async blob => {
          if (file.logo !== DEFAULT_VIDEO_THUMBNAIL && file.logo.startsWith('blob:')) {
            this.objectURLsvc.revokeObjectURL(file.logo);
          }
          file.logo = this.objectURLsvc.createObjectURL(blob);
        });

        videoElem.removeEventListener('timeupdate', captureThumb);
      };

      /* istanbul ignore next. Readon: hard to trigger event */
      videoElem.addEventListener('timeupdate', captureThumb);
    }
  }

  prepareCanvasForThumbnailCapture(videoElem: HTMLVideoElement, file: WGFile): HTMLCanvasElement {
    const canvasElem = document.getElementById('canvas-element') as HTMLCanvasElement;
    videoElem.src = file.fullPath;
    videoElem.load();
    videoElem.muted = true;
    videoElem.play();
    videoElem.currentTime = 4;

    /* istanbul ignore next. Readon: hard to trigger event */
    videoElem.addEventListener('loadedmetadata', () => {
      canvasElem.width = videoElem.videoWidth;
      canvasElem.height = videoElem.videoHeight;
    });

    return canvasElem;
  }
}

interface EventSelectedFile {
  addedFiles: File[];
  rejectedFiles: RejectedFile[];
  source: NgxDropzoneComponent;
}

export enum SupportedFileUploadType {
  image = 'image',
  video = 'video',
}
